// 36 VGÜ-Tatbestände gemäß VGÜ-VO BGBl. II Nr. 27/1997 idgF (RIS-Quelle)
// Kategorien: A = Allgemein, E = Einstellungs/Eignungsuntersuchung, G = Gefährdung, S = Sonder

export type TatbestandKategorie = 'Lärm' | 'Vibration' | 'Staub' | 'Chemisch' | 'Biologisch'
  | 'Physikalisch' | 'Ergonomisch' | 'Psychisch' | 'Allgemein' | 'Sonder';

export interface Tatbestand {
  readonly code:        string;       // z.B. "L1", "V1", "Ch5"
  readonly bezeichnung: string;       // Kurzbezeichnung
  readonly langtext:    string;       // Vollständiger RIS-Text
  readonly kategorie:   TatbestandKategorie;
  readonly intervallMonate: 12 | 24 | 36 | 60; // Regelintervall
  readonly erstAbJahren?: number;     // Erst-VGÜ-Pflicht ab ... Beschäftigungsjahren
  readonly mindestalter?: number;
  readonly pflichtDoku: string[];     // Pflicht-Parameter im Befund
  readonly gesetzlGrundlage: string;  // §-Verweis
  readonly aktiv: boolean;
}

export const TATBESTAENDE: readonly Tatbestand[] = [
  // ── LÄRM ──────────────────────────────────────────────────────────────────
  {
    code: 'L1',
    bezeichnung: 'Lärm ≥ 85 dB(A)',
    langtext: 'Arbeitnehmer/innen, die Lärm von 85 dB(A) oder mehr ausgesetzt sind',
    kategorie: 'Lärm',
    intervallMonate: 36,
    pflichtDoku: ['Audiogramm', 'Tinnitus-Anamnese', 'OHR-Status'],
    gesetzlGrundlage: '§ 5 Abs. 1 Z 1 VGÜ-VO',
    aktiv: true,
  },
  {
    code: 'L2',
    bezeichnung: 'Lärm 80–85 dB(A)',
    langtext: 'Arbeitnehmer/innen, die Lärm von 80 bis unter 85 dB(A) ausgesetzt sind',
    kategorie: 'Lärm',
    intervallMonate: 60,
    pflichtDoku: ['Audiogramm', 'Tinnitus-Anamnese'],
    gesetzlGrundlage: '§ 5 Abs. 1 Z 2 VGÜ-VO',
    aktiv: true,
  },
  // ── VIBRATION ────────────────────────────────────────────────────────────
  {
    code: 'V1',
    bezeichnung: 'Hand-Arm-Vibration',
    langtext: 'Arbeitnehmer/innen, die Hand-Arm-Vibrationen ausgesetzt sind (Tagesexpositionswert ≥ 2,5 m/s²)',
    kategorie: 'Vibration',
    intervallMonate: 36,
    pflichtDoku: ['Vibrations-Anamnese', 'Durchblutung Hände', 'Neurologie Hände'],
    gesetzlGrundlage: '§ 5 Abs. 1 Z 3 VGÜ-VO',
    aktiv: true,
  },
  {
    code: 'V2',
    bezeichnung: 'Ganzkörper-Vibration',
    langtext: 'Arbeitnehmer/innen, die Ganzkörper-Vibrationen ausgesetzt sind (Tagesexpositionswert ≥ 0,5 m/s²)',
    kategorie: 'Vibration',
    intervallMonate: 36,
    pflichtDoku: ['Wirbelsäulen-Anamnese', 'WS-Status'],
    gesetzlGrundlage: '§ 5 Abs. 1 Z 4 VGÜ-VO',
    aktiv: true,
  },
  // ── STAUB ────────────────────────────────────────────────────────────────
  {
    code: 'St1',
    bezeichnung: 'Quarzstaub / Silikose-Risiko',
    langtext: 'Arbeitnehmer/innen, die quarzhältigem Staub ausgesetzt sind',
    kategorie: 'Staub',
    intervallMonate: 24,
    pflichtDoku: ['Röntgen Thorax', 'Spirometrie', 'Staub-Anamnese'],
    gesetzlGrundlage: '§ 5 Abs. 1 Z 5 VGÜ-VO',
    aktiv: true,
  },
  {
    code: 'St2',
    bezeichnung: 'Asbeststäube',
    langtext: 'Arbeitnehmer/innen, die Asbeststäuben ausgesetzt sind oder waren',
    kategorie: 'Staub',
    intervallMonate: 12,
    pflichtDoku: ['Röntgen Thorax', 'Spirometrie', 'CT-Thorax nach Indikation', 'Asbestanamnese'],
    gesetzlGrundlage: '§ 5 Abs. 1 Z 6 VGÜ-VO',
    aktiv: true,
  },
  {
    code: 'St3',
    bezeichnung: 'Sonstige Alveolenstäube',
    langtext: 'Arbeitnehmer/innen, die alveolengängigen Stäuben (ohne Quarz/Asbest) ausgesetzt sind',
    kategorie: 'Staub',
    intervallMonate: 36,
    pflichtDoku: ['Spirometrie', 'Staub-Anamnese'],
    gesetzlGrundlage: '§ 5 Abs. 1 Z 7 VGÜ-VO',
    aktiv: true,
  },
  // ── CHEMISCH ─────────────────────────────────────────────────────────────
  {
    code: 'Ch1',
    bezeichnung: 'Benzol',
    langtext: 'Arbeitnehmer/innen, die Benzol ausgesetzt sind',
    kategorie: 'Chemisch',
    intervallMonate: 12,
    pflichtDoku: ['Blutbild', 'Diff-BB', 'Leberwerte', 'Urin-Benzol-Metabolite'],
    gesetzlGrundlage: '§ 5 Abs. 1 Z 8 VGÜ-VO',
    aktiv: true,
  },
  {
    code: 'Ch2',
    bezeichnung: 'Blei und anorganische Bleiverbindungen',
    langtext: 'Arbeitnehmer/innen, die Blei oder anorganischen Bleiverbindungen ausgesetzt sind',
    kategorie: 'Chemisch',
    intervallMonate: 12,
    pflichtDoku: ['Blutblei', 'Blutbild', 'Nierenwerte', 'Blutdruck'],
    gesetzlGrundlage: '§ 5 Abs. 1 Z 9 VGÜ-VO',
    aktiv: true,
  },
  {
    code: 'Ch3',
    bezeichnung: 'Quecksilber',
    langtext: 'Arbeitnehmer/innen, die Quecksilber oder Quecksilberverbindungen ausgesetzt sind',
    kategorie: 'Chemisch',
    intervallMonate: 12,
    pflichtDoku: ['Urin-Hg', 'Nierenwerte', 'Neurologie'],
    gesetzlGrundlage: '§ 5 Abs. 1 Z 10 VGÜ-VO',
    aktiv: true,
  },
  {
    code: 'Ch4',
    bezeichnung: 'Chrom VI',
    langtext: 'Arbeitnehmer/innen, die sechswertigem Chrom ausgesetzt sind',
    kategorie: 'Chemisch',
    intervallMonate: 12,
    pflichtDoku: ['Urin-Cr', 'Nierenwerte', 'Lungenfunktion', 'Haut-/Schleimhaut-Inspektion'],
    gesetzlGrundlage: '§ 5 Abs. 1 Z 11 VGÜ-VO',
    aktiv: true,
  },
  {
    code: 'Ch5',
    bezeichnung: 'Organische Lösemittel',
    langtext: 'Arbeitnehmer/innen, die organischen Lösemitteln ausgesetzt sind',
    kategorie: 'Chemisch',
    intervallMonate: 24,
    pflichtDoku: ['Leberwerte', 'Nierenwerte', 'Neurologie', 'Lösemittel-Anamnese'],
    gesetzlGrundlage: '§ 5 Abs. 1 Z 12 VGÜ-VO',
    aktiv: true,
  },
  {
    code: 'Ch6',
    bezeichnung: 'Pestizide / Pflanzenschutzmittel',
    langtext: 'Arbeitnehmer/innen, die Pestiziden oder Pflanzenschutzmitteln ausgesetzt sind',
    kategorie: 'Chemisch',
    intervallMonate: 12,
    pflichtDoku: ['Cholinesterase', 'Leberwerte', 'Blutbild', 'Neurologie'],
    gesetzlGrundlage: '§ 5 Abs. 1 Z 13 VGÜ-VO',
    aktiv: true,
  },
  {
    code: 'Ch7',
    bezeichnung: 'Arsen und Arsenverbindungen',
    langtext: 'Arbeitnehmer/innen, die Arsen oder Arsenverbindungen ausgesetzt sind',
    kategorie: 'Chemisch',
    intervallMonate: 12,
    pflichtDoku: ['Urin-As', 'Leberwerte', 'Periphere Neurologie', 'Haut'],
    gesetzlGrundlage: '§ 5 Abs. 1 Z 14 VGÜ-VO',
    aktiv: true,
  },
  {
    code: 'Ch8',
    bezeichnung: 'Cadmium',
    langtext: 'Arbeitnehmer/innen, die Cadmium oder Cadmiumverbindungen ausgesetzt sind',
    kategorie: 'Chemisch',
    intervallMonate: 12,
    pflichtDoku: ['Blut-Cd', 'Urin-Cd', 'Nierenwerte', 'Lungenfunktion'],
    gesetzlGrundlage: '§ 5 Abs. 1 Z 15 VGÜ-VO',
    aktiv: true,
  },
  {
    code: 'Ch9',
    bezeichnung: 'Isocyanate',
    langtext: 'Arbeitnehmer/innen, die Isocyanaten ausgesetzt sind',
    kategorie: 'Chemisch',
    intervallMonate: 12,
    pflichtDoku: ['Spirometrie', 'Peak-Flow', 'Haut-Status', 'Atemwegsanamnese'],
    gesetzlGrundlage: '§ 5 Abs. 1 Z 16 VGÜ-VO',
    aktiv: true,
  },
  {
    code: 'Ch10',
    bezeichnung: 'Vinylchlorid',
    langtext: 'Arbeitnehmer/innen, die Vinylchlorid ausgesetzt sind',
    kategorie: 'Chemisch',
    intervallMonate: 12,
    pflichtDoku: ['Leberwerte', 'Blutbild', 'Durchblutung Hände', 'Oberbauch-Sono'],
    gesetzlGrundlage: '§ 5 Abs. 1 Z 17 VGÜ-VO',
    aktiv: true,
  },
  {
    code: 'Ch11',
    bezeichnung: 'Hartholzstaub',
    langtext: 'Arbeitnehmer/innen, die Hartholzstäuben ausgesetzt sind',
    kategorie: 'Chemisch',
    intervallMonate: 12,
    pflichtDoku: ['HNO-Status', 'Rhinoskopie', 'Nasenzytologie nach Indikation'],
    gesetzlGrundlage: '§ 5 Abs. 1 Z 18 VGÜ-VO',
    aktiv: true,
  },
  // ── BIOLOGISCH ───────────────────────────────────────────────────────────
  {
    code: 'B1',
    bezeichnung: 'Biologische Arbeitsstoffe Risikogruppe 3',
    langtext: 'Arbeitnehmer/innen, die biologischen Arbeitsstoffen der Risikogruppe 3 ausgesetzt sind',
    kategorie: 'Biologisch',
    intervallMonate: 24,
    pflichtDoku: ['Impfstatus', 'Immunstatus', 'Infektionsanamnese'],
    gesetzlGrundlage: '§ 5 Abs. 1 Z 19 VGÜ-VO',
    aktiv: true,
  },
  {
    code: 'B2',
    bezeichnung: 'Nadelstichverletzungs-Risiko (Gesundheitswesen)',
    langtext: 'Arbeitnehmer/innen im Gesundheitswesen mit Risiko für Nadelstich- und Schnittverletzungen',
    kategorie: 'Biologisch',
    intervallMonate: 24,
    pflichtDoku: ['Hep-B-Titer', 'HIV-Aufklärung', 'Impfstatus Hep-B'],
    gesetzlGrundlage: '§ 5 Abs. 1 Z 20 VGÜ-VO',
    aktiv: true,
  },
  // ── PHYSIKALISCH ─────────────────────────────────────────────────────────
  {
    code: 'P1',
    bezeichnung: 'Ionisierende Strahlung',
    langtext: 'Arbeitnehmer/innen, die ionisierender Strahlung ausgesetzt sind (Kategorie A und B)',
    kategorie: 'Physikalisch',
    intervallMonate: 12,
    pflichtDoku: ['Blutbild', 'Diff-BB', 'Augenuntersuchung', 'Dosimetrie-Auswertung'],
    gesetzlGrundlage: '§ 5 Abs. 1 Z 21 VGÜ-VO',
    aktiv: true,
  },
  {
    code: 'P2',
    bezeichnung: 'Ultraschall (Lärm > 20 kHz)',
    langtext: 'Arbeitnehmer/innen, die Ultraschall mit potentiell schädlichen Schalldruckpegeln ausgesetzt sind',
    kategorie: 'Physikalisch',
    intervallMonate: 36,
    pflichtDoku: ['Audiogramm', 'HNO-Status'],
    gesetzlGrundlage: '§ 5 Abs. 1 Z 22 VGÜ-VO',
    aktiv: true,
  },
  {
    code: 'P3',
    bezeichnung: 'Überdruck / Druckluft',
    langtext: 'Arbeitnehmer/innen, die unter Überdruck arbeiten (Taucher, Caissons)',
    kategorie: 'Physikalisch',
    intervallMonate: 12,
    pflichtDoku: ['Herz-Kreislauf-Status', 'Lungenfunktion', 'Audiogramm', 'Röntgen Thorax'],
    gesetzlGrundlage: '§ 5 Abs. 1 Z 23 VGÜ-VO',
    aktiv: true,
  },
  // ── ERGONOMISCH ──────────────────────────────────────────────────────────
  {
    code: 'Er1',
    bezeichnung: 'Manuelle Lastenhandhabung (Heben/Tragen > 25 kg)',
    langtext: 'Arbeitnehmer/innen, die regelmäßig Lasten von mehr als 25 kg heben oder tragen',
    kategorie: 'Ergonomisch',
    intervallMonate: 36,
    pflichtDoku: ['Wirbelsäulen-Status', 'Muskel-Skelett-Anamnese'],
    gesetzlGrundlage: '§ 5 Abs. 1 Z 24 VGÜ-VO',
    aktiv: true,
  },
  {
    code: 'Er2',
    bezeichnung: 'Zwangshaltungen / repetitive Arbeit',
    langtext: 'Arbeitnehmer/innen, die überwiegend in Zwangshaltungen oder mit hochrepetitiven Tätigkeiten arbeiten',
    kategorie: 'Ergonomisch',
    intervallMonate: 36,
    pflichtDoku: ['Muskel-Skelett-Status', 'Ergonomie-Anamnese'],
    gesetzlGrundlage: '§ 5 Abs. 1 Z 25 VGÜ-VO',
    aktiv: true,
  },
  // ── PSYCHISCH ────────────────────────────────────────────────────────────
  {
    code: 'Psy1',
    bezeichnung: 'Arbeitsbedingte psychische Fehlbelastung',
    langtext: 'Arbeitnehmer/innen mit erhöhter psychischer Arbeitsbelastung (Zeitdruck, Nachtarbeit, Schichtarbeit)',
    kategorie: 'Psychisch',
    intervallMonate: 36,
    pflichtDoku: ['Burnout-Screening', 'Schlafanamnese', 'Psychosozialer Status'],
    gesetzlGrundlage: '§ 5 Abs. 1 Z 26 VGÜ-VO',
    aktiv: true,
  },
  {
    code: 'Psy2',
    bezeichnung: 'Nacht- und Schichtarbeit',
    langtext: 'Arbeitnehmer/innen in regelmäßiger Nacht- oder Wechselschichtarbeit',
    kategorie: 'Psychisch',
    intervallMonate: 24,
    pflichtDoku: ['Schlafqualität', 'Herz-Kreislauf-Risiko', 'Magen-Darm-Anamnese', 'Burnout-Screening'],
    gesetzlGrundlage: '§ 5 Abs. 1 Z 27 VGÜ-VO',
    aktiv: true,
  },
  // ── SONDER ───────────────────────────────────────────────────────────────
  {
    code: 'S1',
    bezeichnung: 'Bildschirmarbeit ≥ 3 h/Tag',
    langtext: 'Arbeitnehmer/innen, die überwiegend an Bildschirmgeräten arbeiten (≥ 3 Stunden täglich)',
    kategorie: 'Sonder',
    intervallMonate: 60,
    pflichtDoku: ['Sehtest', 'Augendruckmessung nach Indikation', 'Wirbelsäulen-Screening'],
    gesetzlGrundlage: '§ 68 ASchG i.V.m. BSVA',
    aktiv: true,
  },
  {
    code: 'S2',
    bezeichnung: 'Fahr-, Steuer- und Überwachungstätigkeit',
    langtext: 'Arbeitnehmer/innen mit sicherheitsrelevanten Fahr-, Steuer- und Überwachungstätigkeiten',
    kategorie: 'Sonder',
    intervallMonate: 36,
    pflichtDoku: ['Sehtest', 'Farbsinnprüfung', 'Reaktionstest', 'Herz-Kreislauf-Status'],
    gesetzlGrundlage: '§ 5 Abs. 1 Z 28 VGÜ-VO',
    aktiv: true,
  },
  {
    code: 'S3',
    bezeichnung: 'Arbeiten unter Tage / Tunnel',
    langtext: 'Arbeitnehmer/innen bei Untertage-Arbeiten oder im Tunnelbau',
    kategorie: 'Sonder',
    intervallMonate: 24,
    pflichtDoku: ['Lungenfunktion', 'Röntgen Thorax', 'Audiogramm', 'Herz-Kreislauf-Status'],
    gesetzlGrundlage: '§ 5 Abs. 1 Z 29 VGÜ-VO',
    aktiv: true,
  },
  {
    code: 'S4',
    bezeichnung: 'Absturzgefährdung (Arbeit auf Leitern/Gerüsten)',
    langtext: 'Arbeitnehmer/innen, die regelmäßig absturzgefährdende Tätigkeiten ausführen',
    kategorie: 'Sonder',
    intervallMonate: 36,
    pflichtDoku: ['Schwindel-Anamnese', 'Gleichgewichtstest', 'Sehtest', 'Herz-Kreislauf-Status'],
    gesetzlGrundlage: '§ 5 Abs. 1 Z 30 VGÜ-VO',
    aktiv: true,
  },
  {
    code: 'S5',
    bezeichnung: 'Hitzearbeit / Kältestress',
    langtext: 'Arbeitnehmer/innen, die extremer Hitze (> 28°C WBGT) oder extremer Kälte (< -10°C) ausgesetzt sind',
    kategorie: 'Sonder',
    intervallMonate: 36,
    pflichtDoku: ['Herz-Kreislauf-Status', 'Nierenwerte', 'Thermotoleranz-Anamnese'],
    gesetzlGrundlage: '§ 5 Abs. 1 Z 31 VGÜ-VO',
    aktiv: true,
  },
  {
    code: 'S6',
    bezeichnung: 'Jugendbeschäftigung (unter 18 Jahre)',
    langtext: 'Jugendliche Arbeitnehmer/innen unter 18 Jahren',
    kategorie: 'Allgemein',
    intervallMonate: 12,
    mindestalter: 0,
    pflichtDoku: ['Wachstum/Entwicklung', 'Muskel-Skelett-Status', 'Sehtest', 'Hörstatus'],
    gesetzlGrundlage: '§ 51 KJBG',
    aktiv: true,
  },
  {
    code: 'S7',
    bezeichnung: 'Einstellungsuntersuchung',
    langtext: 'Untersuchung vor Aufnahme der Beschäftigung auf Eignung für die vorgesehene Tätigkeit',
    kategorie: 'Allgemein',
    intervallMonate: 60,
    erstAbJahren: 0,
    pflichtDoku: ['Vollstatus', 'EKG', 'Sehtest', 'Audiogramm', 'Labor-Basisprofil'],
    gesetzlGrundlage: '§ 4 Abs. 1 VGÜ-VO',
    aktiv: true,
  },
  {
    code: 'S8',
    bezeichnung: 'Allgemeine VGÜ (Intervall-Vorsorge)',
    langtext: 'Regelmäßige ärztliche Vorsorgeuntersuchung ohne spezifische Gefährdung',
    kategorie: 'Allgemein',
    intervallMonate: 36,
    pflichtDoku: ['Anamnese', 'Blutbild', 'Harn-Status', 'Blutdruck', 'BMI', 'Sehtest'],
    gesetzlGrundlage: '§ 4 Abs. 2 VGÜ-VO',
    aktiv: true,
  },
] as const;

export const TATBESTAND_MAP = new Map<string, Tatbestand>(
  TATBESTAENDE.map(t => [t.code, t])
);

export const KATEGORIEN: readonly TatbestandKategorie[] = [
  'Allgemein', 'Lärm', 'Vibration', 'Staub', 'Chemisch',
  'Biologisch', 'Physikalisch', 'Ergonomisch', 'Psychisch', 'Sonder',
];
